import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useTenant } from "../../../../Context/Tenant/TenantContext";
import { ADD_TENANT, TOGGLE_SHOW_INVITE_USER_DIALOG } from "../TenantEvents";
import InnerLoader from "../../../Shared/Loader/InnerLoader";
import { OverlayLoader } from "../../../Shared/Loader/OverlayLoader";
import { inviteTenant } from "../../../../API/Tenant/Tenant";
import { useGlobalContext } from "../../../../Renderers/GlobalContext";
import ErrorMessage from "../../../Shared/Error/ErrorMessage";
import { useTranslation } from "react-i18next";
import { Switch } from "@progress/kendo-react-inputs";

const InviteTenantDialog = () => {
    const { state: globalState } = useGlobalContext();
    const { state, dispatch } = useTenant();
    const [isSaving, setIsSaving] = useState(false);
    const [tenantName, setTenantName] = useState("");
    const [email, setEmail] = useState("");
    const projects = state.projects.filter(project => project.isActive && project.masterApp);
    const [selectedProjectId, setSelectedProjectId] = useState(projects[0]?._id || null);
    const [selectedInstanceId, setSelectedInstanceId] = useState(selectedProjectId ? projects[0].instances[0]?._id : null);
    const [validity, setValidity] = useState({
        tenantName: true,
        duplicateTenantName: true,
        longerTenantName: true,
        email: true,
        duplicateEmail: true
    });

    const [enableTenantTestEnvironment, setEnableTenantTestEnvironment] = useState(false);

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const handleTenantNameChange = (e) => {
        const value = e.target.value.trim();
        setTenantName(e.target.value);
        setValidity((prev) => ({
            ...prev,
            tenantName: !!value,
            duplicateTenantName: !state.tenants.some((tenant) => tenant.tenantName.trim().toLowerCase() === value.toLowerCase()),
            longerTenantName: value.length <= 64
        }));
    };

    const handleEmailChange = (e) => {
        const value = e.target.value.trim();
        setEmail(value);
        setValidity((prev) => ({
            ...prev,
            email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
            duplicateEmail: !state.tenants.some((tenant) => tenant.tenantEmail.trim().toLowerCase() === value.toLowerCase())
        }));
    };

    const handleSelectedProjectChange = (e) => {
        const project = projects.find(project => project._id === e.target.value._id);
        setSelectedProjectId(e.target.value._id);
        setSelectedInstanceId(project?.instances[0]?._id || null);
    };

    const handleSelectedInstanceChange = (e) => {
        setSelectedInstanceId(e.target.value._id);
    };

    const handleEnableTenantTestEnvironmentChange = (e) => {
        setEnableTenantTestEnvironment(e.target.value);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!validity.tenantName || !validity.duplicateTenantName || !validity.email || !validity.duplicateEmail) return;
        setIsSaving(true);
        try {
            const response = await inviteTenant({
                tenantName: tenantName,
                tenantEmail: email.toLowerCase(),
                masterOrganizationId: globalState.organization._id,
                masterProjectId: selectedProjectId,
                masterAppId: selectedInstanceId,
                masterUserId: globalState.user._id,
                enableTenantTestEnvironment: enableTenantTestEnvironment
            });
            if (response.error) {
                throw new Error(response.error);
            } else {
                response.expanded = true
                dispatch({ type: ADD_TENANT, payload: response });
            }
        } catch (error) {
            console.error("Error inviting tenant:", error.message);
        } finally {
            setIsSaving(false);
            dispatch({ type: TOGGLE_SHOW_INVITE_USER_DIALOG });
        }
    }

    const getProjectInstances = (projectId) => {
        const project = projects.find(project => project._id === projectId);
        if (project) {
            return project.instances.filter(instance => instance.Published && instance.isActive);
        }
        return [];
    }

    return (
        <Dialog
            title={t("invite_tenant")}
            onClose={() => dispatch({ type: TOGGLE_SHOW_INVITE_USER_DIALOG })}
            width={500}
            height={500}
        >
            <form>
                {isSaving && <OverlayLoader />}
                <div className="mb-3">
                    <label className="form-label" required>
                        {t("tenant_name")}
                    </label>
                    {!validity.tenantName && (
                        <ErrorMessage
                            visible={!validity.tenantName}
                            error={t("tenant_name_required")}
                        />
                    )}
                    {!validity.duplicateTenantName && (
                        <ErrorMessage
                            visible={!validity.duplicateTenantName}
                            error={t("tenant_name_already_exists")}
                        />
                    )}
                    {!validity.longerTenantName && (
                        <ErrorMessage
                            visible={!validity.longerTenantName}
                            error={t("tenant_name_max_length")}
                        />
                    )}
                    <input
                        type="text"
                        className={"form-control"}
                        value={tenantName}
                        onChange={handleTenantNameChange}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label" required>
                        {t("invite_with_email")}
                    </label>
                    {!validity.email && (
                        <ErrorMessage
                            visible={!validity.email}
                            error={t("invalid_email")}
                        />
                    )}
                    {!validity.duplicateEmail && (
                        <ErrorMessage
                            visible={!validity.duplicateEmail}
                            error={t("email_already_exists")}
                        />
                    )}
                    <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={handleEmailChange}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label">
                        {t("choose_app")}
                    </label>
                    <DropDownList
                        data={projects}
                        textField="name"
                        dataItemKey="_id"
                        value={projects.find(project => project._id === selectedProjectId)}
                        onChange={handleSelectedProjectChange}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label">
                        {t("choose_source_environment")}
                    </label>
                    <DropDownList
                        data={getProjectInstances(selectedProjectId)}
                        textField="instanceName"
                        dataItemKey="_id"
                        value={projects.find(project => project._id === selectedProjectId)?.instances.find(instance => instance._id === selectedInstanceId)}
                        onChange={handleSelectedInstanceChange}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label">
                        {t("enable_tenant_test_environment")}
                    </label>
                    <Switch
                        size={"medium"}
                        value={enableTenantTestEnvironment}
                        className="d-block"
                        name="enableTenantTestEnvironment"
                        onChange={handleEnableTenantTestEnvironmentChange}
                    />
                </div>
            </form>
            <DialogActionsBar layout="end">
                <div className="mb-3 me-3">
                    {isSaving && <InnerLoader borderColor="green" />}
                    <button
                        className="btn btn-secondary"
                        onClick={() => dispatch({ type: TOGGLE_SHOW_INVITE_USER_DIALOG })}
                    >
                        {t("cancel_button")}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!validity.tenantName || !validity.duplicateTenantName || !validity.longerTenantName || !validity.email || !validity.duplicateEmail || isSaving || !selectedProjectId || !selectedInstanceId || !tenantName || !email}
                        className="btn btn-primary"
                    >
                        {t("send_invite_button")}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default InviteTenantDialog;
